/**
 * Send hold — THE ONE predicate for "may this workspace's send go out on this channel?"
 * (F-24 / MAR-24, MAR-241).
 *
 * Two workspace states hold a send, and they are different facts:
 *
 *   'disabled' — `mode: 'disabled'`, an ADMIN state ("Marva admins manage that state"). Nothing
 *                goes out to a shopper. A withheld decision is `suppressed` with disposition
 *                `{blocked, source:'mode', reasonCode:'workspace_disabled'}` — never `shadowed`
 *                (the merchant is not evaluating Marva), never `failed` (nothing broke), never
 *                a contact. A withheld reply records `sendState: 'withheld_disabled'`.
 *   'shadow'   — `mode: 'shadow'`, or the channel listed in `shadowedChannels` (per-channel
 *                billing shadow, pricing-model-prd §5.6; the `{ email: true }` map, an array is
 *                accepted too). Nothing goes out; the decision is recorded `shadowed`, a reply
 *                `withheld_shadow`.
 *
 * `disabled` dominates `shadow`: an admin stop is the stronger fact, and recording it as
 * Shadow would tell the merchant they are previewing Marva when they are not.
 *
 * Before this module `mode: 'disabled'` stopped NO sends — `shouldShadow` matched
 * `mode === 'shadow'` alone, so every send path read a disabled workspace as live, and the
 * reply paths (sms-reply-handler, email-reply-handler, websocket-chat's templated frames) did
 * not honour shadow either (MAR-241). Every shopper-facing send site now asks this function,
 * and `shadow-interceptor.shouldShadow` is defined on it, so a site still on `shouldShadow`
 * holds a disabled workspace too.
 *
 * Exempt, deliberately: the STOP/HELP compliance replies (sms-webhook-handler — a carrier
 * obligation to the shopper, not Marva speaking), merchant-facing mail and notifications, and a
 * merchant's own test sends.
 *
 * A row that could not be READ is the caller's to fail closed on (MAR-24 review): the gateway
 * withholds `workspace_unreadable`, the SMS redrive rethrows (the text stays scheduled),
 * sms-reply-handler and websocket-chat record the reply `failed` / `workspace_unreadable` without
 * sending, email-reply-handler aborts.
 *
 * A row that is DEFINITELY ABSENT — a strongly consistent read found no item — is the workspace
 * having been DELETED, and a workspace that no longer exists sends nothing (MAR-481). `sendHoldOf`
 * cannot see that (it takes a row, and `null` there still holds nothing — merchant reads and the
 * reply vocabulary pass `null` for "no row loaded"), so a SEND SITE asks `sendHoldOfRead` with
 * its `{ ok: true, item }` read instead: an absent item is the `deleted` hold. The gateway
 * settles it `suppressed` + `{blocked, source:'mode', reasonCode:'workspace_deleted'}` (gsi3
 * removed, never a contact) — before MAR-481 `sendHoldOf(null)` read as live, a scheduled replay
 * for a deleted workspace went down the live path, and the send-time enforcer applied the WIDEST
 * default authority to it (`AuthorityRowMissing`). A reply site sends nothing and records nothing:
 * the tenant whose receipt it would be is gone.
 *
 * Dependency-free on purpose (no require at all): `reply-vocabulary` builds on it and the web app
 * bundles that.
 *
 * @module send-hold
 */

'use strict';

const SEND_HOLDS = Object.freeze({
  DISABLED: 'disabled',
  SHADOW: 'shadow',
  /** MAR-481: a strongly consistent read found no workspace row — only `sendHoldOfRead` returns it. */
  DELETED: 'deleted'
});

/** The disposition reason a disabled workspace's withheld decision carries. */
const WORKSPACE_DISABLED_REASON = 'workspace_disabled';

/**
 * The reason a decision ALREADY DISPATCHED live carries when its channel is shadowed by the time
 * a deferred send is re-driven (the quiet-hours SMS redrive) — e.g. overage exhaustion set
 * `shadowedChannels.sms` overnight. `suppressed` + source `mode`: the decision was made and
 * dispatched live, so it is not rewritten as a shadow preview; the text just does not go.
 */
const WORKSPACE_SHADOWED_REASON = 'workspace_shadowed';

/** The workspace row could not be read, so whether it may send is unknown — fail closed. */
const WORKSPACE_UNREADABLE_REASON = 'workspace_unreadable';

/**
 * MAR-481: the workspace row is DEFINITELY absent (a strongly consistent read returned no item) —
 * the workspace was deleted, so its still-queued sends never go out. Not `workspace_unreadable`:
 * that is "we could not tell", which re-defers a scheduled replay; this is a fact, and terminal.
 */
const WORKSPACE_DELETED_REASON = 'workspace_deleted';

/** The disposition source for a hold the workspace's MODE produced (not governance, not authority). */
const MODE_DISPOSITION_SOURCE = 'mode';

/** The reply `sendState` each hold records. */
const HOLD_REPLY_SEND_STATES = Object.freeze({
  [SEND_HOLDS.DISABLED]: 'withheld_disabled',
  [SEND_HOLDS.SHADOW]: 'withheld_shadow'
});

/**
 * Which hold, if any, applies to a send on `channel` from this workspace row.
 *
 * @param {object|null|undefined} workspace - a workspace row the caller already loaded
 * @param {'chat'|'email'|'sms'|string|null} [channel] - per-channel shadow applies only with one
 * @returns {'disabled'|'shadow'|null}
 */
function sendHoldOf(workspace, channel = null) {
  if (!workspace || typeof workspace !== 'object') return null;
  if (workspace.mode === SEND_HOLDS.DISABLED) return SEND_HOLDS.DISABLED;
  if (workspace.mode === SEND_HOLDS.SHADOW) return SEND_HOLDS.SHADOW;
  const shadowed = workspace.shadowedChannels;
  if (channel && shadowed) {
    const listed = Array.isArray(shadowed) ? shadowed.includes(channel) : Boolean(shadowed[channel]);
    if (listed) return SEND_HOLDS.SHADOW;
  }
  return null;
}

/**
 * The hold for a send site's workspace READ (MAR-481) — `sendHoldOf` plus the one fact a row
 * cannot carry: that there is no row.
 *
 * ⚠️ The read MUST be strongly consistent (`ConsistentRead: true`): an eventually consistent miss
 * is not proof of absence (a just-created workspace), and `deleted` is terminal.
 *
 * @param {{ok: true, item: object|null}} read - a SUCCESSFUL read. An unreadable workspace
 *   (`ok: false`) is the caller's to fail closed on first (re-defer / rethrow / record `failed`),
 *   so passing one is a programming error and throws rather than guessing.
 * @param {'chat'|'email'|'sms'|string|null} [channel]
 * @returns {'deleted'|'disabled'|'shadow'|null}
 */
function sendHoldOfRead(read, channel = null) {
  if (!read || read.ok !== true) {
    throw new TypeError('sendHoldOfRead needs a successful workspace read — fail closed on an unreadable one first');
  }
  if (read.item === null || read.item === undefined) return SEND_HOLDS.DELETED;
  return sendHoldOf(read.item, channel);
}

/**
 * The reply `sendState` for a hold (`withheld_disabled` | `withheld_shadow`), or null for none —
 * and null for `deleted`, whose reply is neither sent nor recorded (the tenant is gone).
 */
function replySendStateForHold(hold) {
  return Object.prototype.hasOwnProperty.call(HOLD_REPLY_SEND_STATES, hold) ? HOLD_REPLY_SEND_STATES[hold] : null;
}

/**
 * The gateway's ordinary withhold result for a disabled workspace — `success:false, skipped:true`
 * plus `dispositionSource`, which BOTH `finaliseImmediateDecision` and `scheduled-executor`
 * settle as `suppressed` (gsi3 removed, NOT_SENT, so `reachedCustomer` never counts it).
 */
function disabledWithholdResult() {
  return modeWithholdResult(WORKSPACE_DISABLED_REASON);
}

/** The gateway's withhold for a deleted workspace (MAR-481) — `suppressed` / `workspace_deleted`. */
function deletedWithholdResult() {
  return modeWithholdResult(WORKSPACE_DELETED_REASON);
}

/** The same withhold shape for any mode reason (`workspace_disabled|_shadowed|_unreadable|_deleted`). */
function modeWithholdResult(reason) {
  return {
    success: false,
    skipped: true,
    reason,
    dispositionSource: MODE_DISPOSITION_SOURCE
  };
}

/**
 * The withhold for a deferred send re-driven after its decision dispatched live: ANY hold stops
 * it — deleted as `workspace_deleted` (MAR-481), disabled as `workspace_disabled`, shadow as
 * `workspace_shadowed`. Null for no hold.
 */
function redriveWithholdResult(hold) {
  if (hold === SEND_HOLDS.DELETED) return modeWithholdResult(WORKSPACE_DELETED_REASON);
  if (hold === SEND_HOLDS.DISABLED) return modeWithholdResult(WORKSPACE_DISABLED_REASON);
  if (hold === SEND_HOLDS.SHADOW) return modeWithholdResult(WORKSPACE_SHADOWED_REASON);
  return null;
}

module.exports = {
  SEND_HOLDS,
  WORKSPACE_DISABLED_REASON,
  WORKSPACE_SHADOWED_REASON,
  WORKSPACE_UNREADABLE_REASON,
  WORKSPACE_DELETED_REASON,
  MODE_DISPOSITION_SOURCE,
  HOLD_REPLY_SEND_STATES,
  sendHoldOf,
  sendHoldOfRead,
  replySendStateForHold,
  disabledWithholdResult,
  deletedWithholdResult,
  modeWithholdResult,
  redriveWithholdResult
};
